import { Bell, X } from "lucide-react";
import { useCallback, useRef, useState } from "react";

import { useDismissable } from "@/components/layout/useDismissable";

import { FeedbackForm } from "./FeedbackForm";
import "./FeedbackButton.css";

/**
 * De feedbackknop in de schil: op elk scherm bereikbaar, met de context van dat
 * scherm eronder. In stijl A een chip "Feedback" in de lichte kop, zoals de
 * andere onderdelen daar.
 *
 * Twee keuzes die het gedrag bepalen:
 *
 *  - **De knop werkt ook met alleen leesrechten.** Dat is de hele reden dat de
 *    backend hier een uitzondering maakt op het schrijfslot: de meelezer die
 *    niets mag wijzigen is juist degene die iets te zeggen heeft.
 *  - **De context gaat automatisch mee.** De gebruiker hoeft niet uit te leggen
 *    waar hij stond; scherm, URL, verslagjaar en run-id staan er al bij (zie
 *    `useFeedbackContext`). Het formulier zelf staat in `FeedbackForm`.
 *
 * Het paneel blijft bestaan als het dicht is, zodat een half geschreven
 * opmerking niet verdwijnt bij een klik ernaast. Na het versturen begint het
 * de volgende keer leeg.
 */
export function FeedbackButton() {
  const [open, setOpen] = useState(false);
  /** Nieuwe sleutel = nieuw, leeg formulier; alleen na een verstuurde opmerking. */
  const [formulier, setFormulier] = useState(0);
  const verstuurd = useRef(false);
  const ref = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    if (verstuurd.current) {
      verstuurd.current = false;
      setFormulier((waarde) => waarde + 1);
    }
  }, []);
  useDismissable(ref, open, close);

  return (
    <div className="atl-feedback" ref={ref}>
      <button
        type="button"
        className="atl-feedback__trigger"
        aria-expanded={open}
        aria-haspopup="dialog"
        // Op een kleiner venster is de knop alleen het belletje (Header.css).
        title="Feedback op dit scherm"
        onClick={() => (open ? close() : setOpen(true))}
      >
        <Bell size={15} strokeWidth={1.75} aria-hidden />
        <span>Feedback</span>
      </button>

      <div className="atl-feedback__panel" role="dialog" aria-label="Feedback op dit scherm" hidden={!open}>
        <header className="atl-feedback__header">
          <h2>Wat valt je op?</h2>
          <button type="button" onClick={close} aria-label="Sluiten">
            <X size={16} strokeWidth={2} aria-hidden />
          </button>
        </header>
        <FeedbackForm
          key={formulier}
          id="feedback-fotos"
          onNavigate={close}
          onSent={() => {
            verstuurd.current = true;
          }}
        />
      </div>
    </div>
  );
}
